import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import {
  startDismissBrandLearnedInsight,
  startGetBrandLearnedInsights,
  startMarkBrandInsightsLastSeen,
} from "../../../../actions/brandLearnedInsights";
import { getBrandInsightsLastViewedAtOnEnter } from "../../../../utils/brandInsightsLastViewed";
import { getPlaybookLastSeenAt } from "../../../../utils/brandInsightsUnread";
import { useApplyBrandInsight } from "./useApplyBrandInsight";
import { shouldUseDemoBrandLearnedInsights } from "../../../../utils/brandInsightsDemo";

export const useBrandLearnedInsights = () => {
  const dispatch = useDispatchApp();
  const { userID, agencyID } = dispatch(getPathDatabaseByRole());
  const isDemoAdvertiser = shouldUseDemoBrandLearnedInsights({
    advertiserID: userID,
    agencyID,
    shopID: userID,
  });
  const [lastViewedAtOnEnter, setLastViewedAtOnEnter] = useState(0);
  const [playbook, setPlaybook] = useState({ positive: [], negative: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [dismissingKey, setDismissingKey] = useState("");
  const markedSeenRef = useRef(false);
  const {
    applyInsight,
    proposal,
    isProposing,
    isConfirming,
    applyError,
    openApply,
    closeApply,
    confirmApply: confirmApplyInsight,
    retryApply,
  } = useApplyBrandInsight();

  const loadPlaybook = useCallback(async () => {
    const response = await dispatch(startGetBrandLearnedInsights());
    setPlaybook(response.data || { positive: [], negative: [] });
    return response.data;
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      const data = await loadPlaybook();
      if (cancelled) {
        return;
      }
      setLastViewedAtOnEnter(
        getBrandInsightsLastViewedAtOnEnter(userID, getPlaybookLastSeenAt(data))
      );
      setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [loadPlaybook, userID]);

  useEffect(() => {
    if (!userID || isDemoAdvertiser || markedSeenRef.current) return;
    markedSeenRef.current = true;
    dispatch(startMarkBrandInsightsLastSeen());
  }, [dispatch, isDemoAdvertiser, userID]);

  const hasInsights = useMemo(
    () =>
      (playbook.positive || []).length + (playbook.negative || []).length > 0,
    [playbook]
  );

  const confirmApply = useCallback(async () => {
    const result = await confirmApplyInsight();
    if (result?.ok && result.data) {
      setPlaybook(result.data);
    }
    return result;
  }, [confirmApplyInsight]);

  const dismiss = useCallback(
    async ({ polarity, index } = {}) => {
      if (!polarity || !Number.isInteger(index)) {
        return { ok: false };
      }
      setDismissingKey(`${polarity}:${index}`);
      const result = await dispatch(
        startDismissBrandLearnedInsight({ polarity, index })
      );
      if (result?.ok) {
        setPlaybook(result.data || { positive: [], negative: [] });
      }
      setDismissingKey("");
      return result;
    },
    [dispatch]
  );

  return {
    playbook,
    lastViewedAtOnEnter,
    isLoading,
    hasInsights,
    dismissingKey,
    dismiss,
    applyInsight,
    proposal,
    isProposing,
    isConfirming,
    applyError,
    openApply,
    closeApply,
    confirmApply,
    retryApply,
  };
};
